import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  Tabs,
  Tab,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { divisionService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import UploadLogStatusChip from '../components/UploadLogStatusChip'
import InstructionCard from '../components/common/InstructionCard'
import {
  divisionDefinitionUploadInstructions,
  divisionAssignmentUploadInstructions,
} from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'

const BRAND_RED = '#e90000'
const BRAND_RED_HOVER = '#cc0000'
const CARD_SHADOW = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'

const validateCsvFile = (file) =>
  file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))

const DivisionUploadPage = () => {
  const [definitionFile, setDefinitionFile] = useState(null)
  const [assignmentFile, setAssignmentFile] = useState(null)
  const [definitionDragOver, setDefinitionDragOver] = useState(false)
  const [assignmentDragOver, setAssignmentDragOver] = useState(false)
  const [definitionLoading, setDefinitionLoading] = useState(false)
  const [assignmentLoading, setAssignmentLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [batchSize, setBatchSize] = useState(50)
  const [activeTab, setActiveTab] = useState(0)
  const [assignmentResult, setAssignmentResult] = useState(null)

  const [uploadLogs, setUploadLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const logsFetchInFlightRef = useRef(false)

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) return
    logsFetchInFlightRef.current = true
    try {
      if (!silent) setLoadingLogs(true)
      const response = await divisionService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
      })
      const logsData =
        response.items || response.data?.items || response.data || response.logs || response
      const totalCount =
        response.pagination?.total ||
        response.data?.pagination?.total ||
        response.totalCount ||
        response.total ||
        (Array.isArray(logsData) ? logsData.length : 0)
      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) setLoadingLogs(false)
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  useEffect(() => {
    if (activeTab === 1) fetchUploadLogs()
  }, [activeTab, logsPage, logsRowsPerPage, fetchUploadLogs])

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setMessage(null)
    if (newValue === 1) {
      fetchUploadLogs()
    }
  }

  const handleShowErrorDetails = async (logId) => {
    try {
      const response = await divisionService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage)
  }

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10))
    setLogsPage(0)
  }

  const createDragHandlers = (setDragOver, setFile) => ({
    onDragOver: (e) => {
      e.preventDefault()
      e.stopPropagation()
    },
    onDragEnter: (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(true)
    },
    onDragLeave: (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(false)
    },
    onDrop: (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(false)
      const droppedFile = e.dataTransfer.files?.[0]
      if (!droppedFile) return
      if (validateCsvFile(droppedFile)) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    },
  })

  const handleFileSelect = (setFile) => (e) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    if (validateCsvFile(selected)) {
      setFile(selected)
    } else {
      setMessage({ type: 'error', text: 'Please upload a CSV file only' })
    }
  }

  const handleDefinitionUpload = async () => {
    if (!definitionFile) {
      setMessage({ type: 'error', text: 'Please select a division definitions CSV file' })
      return
    }
    try {
      setDefinitionLoading(true)
      const result = await divisionService.importDivisions({
        file: definitionFile,
        batchSize: batchSize.toString(),
      })
      if (result.success && result.message) {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('division'),
          showHistoryLink: true,
        })
        setDefinitionFile(null)
        fetchUploadLogs()
      } else {
        setMessage({ type: 'error', text: 'Failed to start division import' })
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading divisions',
      })
    } finally {
      setDefinitionLoading(false)
    }
  }

  const handleAssignmentUpload = async () => {
    if (!assignmentFile) {
      setMessage({ type: 'error', text: 'Please select a product assignment CSV file' })
      return
    }
    try {
      setAssignmentLoading(true)
      setAssignmentResult(null)
      const result = await divisionService.importDivisionAssignment(assignmentFile)
      setAssignmentResult(result)
      setAssignmentFile(null)
      setMessage({
        type: 'success',
        text:
          result?.message ||
          `Division assignment completed. Updated: ${result?.updated ?? 0}, Failed: ${result?.failures?.length ?? 0}`,
        showHistoryLink: true,
      })
      fetchUploadLogs()
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading division assignment CSV',
      })
    } finally {
      setAssignmentLoading(false)
    }
  }

  const downloadDefinitionSample = () => {
    const csv = 'name\nLoose Commodity\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'division-definitions-sample.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const downloadAssignmentSample = async () => {
    try {
      await divisionService.downloadAssignmentSample()
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to download sample CSV',
      })
    }
  }

  const renderDropzone = ({
    file,
    setFile,
    isDragOver,
    setDragOver,
    inputId,
    title,
  }) => {
    const dragHandlers = createDragHandlers(setDragOver, setFile)
    return (
      <Box
        {...dragHandlers}
        sx={{
          border: '2px dashed',
          borderColor: isDragOver ? BRAND_RED : '#ccc',
          borderRadius: 2,
          p: 6,
          textAlign: 'center',
          bgcolor: isDragOver ? '#fff5f5' : '#fafafa',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: BRAND_RED,
            bgcolor: '#fff5f5',
          },
        }}
        onClick={() => document.getElementById(inputId).click()}
      >
        <CloudUploadIcon sx={{ fontSize: 64, color: isDragOver ? BRAND_RED : '#999', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragOver ? 'Drop file here' : title || 'Drag & Drop your CSV file here'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          or click to browse from your computer
        </Typography>

        <input
          accept=".csv"
          style={{ display: 'none' }}
          id={inputId}
          type="file"
          onChange={handleFileSelect(setFile)}
          onClick={(e) => {
            e.target.value = null
          }}
        />

        {file && (
          <Chip
            label={file.name}
            onDelete={(e) => {
              e.stopPropagation()
              setFile(null)
            }}
            color="primary"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        )}
      </Box>
    )
  }

  const renderUploadActions = ({ file, loading, onUpload, showBatchSize = false }) => (
    <Box
      sx={{
        mt: 4,
        display: 'flex',
        justifyContent: showBatchSize ? 'space-between' : 'flex-end',
        alignItems: 'center',
      }}
    >
      {showBatchSize && (
        <TextField
          label="Batch Size"
          type="number"
          size="small"
          value={batchSize}
          onChange={(e) => setBatchSize(parseInt(e.target.value, 10) || 50)}
          sx={{ width: 120 }}
          InputProps={{ inputProps: { min: 10, max: 1000 } }}
        />
      )}
      <Button
        variant="contained"
        onClick={(e) => {
          e.stopPropagation()
          onUpload()
        }}
        disabled={!file || loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        sx={{
          bgcolor: BRAND_RED,
          '&:hover': { bgcolor: BRAND_RED_HOVER },
          px: 4,
          py: 1.5,
          fontWeight: 'bold',
        }}
      >
        {loading ? 'Uploading...' : 'Upload File'}
      </Button>
    </Box>
  )

  const renderStatusAlert = () =>
    message ? (
      <UploadImportStatusAlert
        severity={message.type}
        onClose={() => setMessage(null)}
        showHistoryLink={message.showHistoryLink}
        onViewUploadHistory={handleViewUploadHistory}
      >
        {message.text}
      </UploadImportStatusAlert>
    ) : null

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
          Division Upload
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload division definitions and assign products via CSV
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: BRAND_RED },
            '& .Mui-selected': { color: `${BRAND_RED} !important` },
          }}
        >
          <Tab
            icon={<CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
            label="Division Definitions"
            iconPosition="start"
          />
          {/* Product Assignment tab temporarily hidden
          <Tab
            icon={<AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
            label="Product Assignment"
            iconPosition="start"
          />
          */}
          <Tab
            icon={<HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
            label="Upload History"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2, boxShadow: CARD_SHADOW }}>
              <CardContent sx={{ p: 4 }}>
                {renderStatusAlert()}
                {renderDropzone({
                  file: definitionFile,
                  setFile: setDefinitionFile,
                  isDragOver: definitionDragOver,
                  setDragOver: setDefinitionDragOver,
                  inputId: 'division-definitions-csv-upload',
                })}
                {renderUploadActions({
                  file: definitionFile,
                  loading: definitionLoading,
                  onUpload: handleDefinitionUpload,
                  showBatchSize: true,
                })}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Sample CSV
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Download a sample CSV to see the required format.
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                  onClick={downloadDefinitionSample}
                  sx={{
                    color: '#e90000',
                    borderColor: '#e90000',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#E8F5E9' },
                    py: 1.2,
                  }}
                >
                  Download Sample
                </Button>
              </CardContent>
            </Card>

            <InstructionCard
              {...divisionDefinitionUploadInstructions}
              icon={<InfoIcon />}
            />
          </Grid>
        </Grid>
      )}

      

      {activeTab === 1 && (
        <Card sx={{ borderRadius: 2, boxShadow: CARD_SHADOW }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Upload History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View the status and details of your recent division uploads
                </Typography>
              </Box>
              <UploadHistoryRefreshButton onClick={fetchUploadLogs} loading={loadingLogs} />
            </Box>

            <UploadHistoryTableWrapper
              loading={loadingLogs}
              isEmpty={uploadLogs.length === 0}
              emptyState={
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HistoryIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    No Upload History
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Your division uploads will appear here after you upload CSV files.
                  </Typography>
                </Box>
              }
            >
              <>
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: 600, overflow: 'auto', boxShadow: 'none', border: '1px solid #e0e0e0' }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, bgcolor: '#f8f9fb' }}>
                          File Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 120, bgcolor: '#f8f9fb' }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">
                          Total
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">
                          Success
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">
                          Failed
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, bgcolor: '#f8f9fb' }}>
                          Upload Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadLogs.map((log) => (
                        <TableRow
                          key={log.id}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fb' },
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudUploadIcon sx={{ fontSize: 16, color: '#666' }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {log.file_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <UploadLogStatusChip status={log.status} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {log.total_rows}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                              {log.successful_imports}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {log.failed_imports > 0 ? (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                                {log.failed_imports}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999' }}>
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              {formatDateTime(log.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {log.failed_imports > 0 ? (
                              <Tooltip title="View error details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleShowErrorDetails(log.id)}
                                  sx={{
                                    color: '#f44336',
                                    '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' },
                                  }}
                                >
                                  <ErrorIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Box sx={{ width: 32, height: 32, display: 'inline-block' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={logsTotalCount}
                  page={logsPage}
                  onPageChange={handleLogsPageChange}
                  rowsPerPage={logsRowsPerPage}
                  onRowsPerPageChange={handleLogsRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{ borderTop: 'none' }}
                />
              </>
            </UploadHistoryTableWrapper>
          </CardContent>
        </Card>
      )}

      <UploadErrorDetailsDialog
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
      />
    </Container>
  )
}

export default DivisionUploadPage
